import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DepartmentListResponse, DepartmentModel } from "../../models/department.model";

interface DepartmentListParams {
    name?: string | null;
    page?: number;
    limit?: number;
    sort?: string | null;
    direction?: string | null;
}

@Injectable({
    providedIn: "root",
})
export class DepartmentService {
    private apiUrl = "/departments";

    #http = inject(HttpClient);

    getDepartments(params?: DepartmentListParams): Observable<DepartmentListResponse> {
        return this.#http.get<unknown>(this.apiUrl, {
            params: this.buildListParams(params),
        }).pipe(
            map((response) => this.normalizeDepartmentsResponse(response, params)),
        );
    }

    private buildListParams(params?: DepartmentListParams): HttpParams {
        let httpParams = new HttpParams();

        if (params?.name != null && params.name !== "") {
            httpParams = httpParams.set("name", params.name);
        }

        if (params?.page != null) {
            httpParams = httpParams.set("page", String(params.page));
        }

        if (params?.limit != null) {
            httpParams = httpParams.set("limit", String(params.limit));
        }

        if (params?.sort != null && params.sort !== "") {
            httpParams = httpParams.set("sort", params.sort);
        }

        if (params?.direction != null && params.direction !== "") {
            httpParams = httpParams.set("direction", params.direction);
        }

        return httpParams;
    }

    private normalizeDepartmentsResponse(
        response: unknown,
        params?: DepartmentListParams,
    ): DepartmentListResponse {
        if (
            response !== null &&
            typeof response === "object" &&
            !Array.isArray(response)
        ) {
            const departmentResponse = response as Record<string, unknown>;

            if (Array.isArray(departmentResponse.data)) {
                return {
                    data: departmentResponse.data as DepartmentModel[],
                    page: Number(departmentResponse.page ?? params?.page ?? 1),
                    limit: Number(departmentResponse.limit ?? params?.limit ?? 10),
                    total: Number(departmentResponse.total ?? departmentResponse.data.length),
                    total_pages: Number(departmentResponse.total_pages ?? 1),
                };
            }
        }

        const data = Array.isArray(response) ? response as DepartmentModel[] : [];
        const limit = params?.limit ?? 10;

        return {
            data,
            page: params?.page ?? 1,
            limit,
            total: data.length,
            total_pages: Math.max(1, Math.ceil(data.length / limit)),
        };
    }
}
